import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import friendService from '../services/friendService';
import PortalRing from './stories/PortalRing';
import { discoFrame, fillFor } from './wall/wallTokens';
import { revealModal, dismissModal } from './messages/messagesMotion';
import BottomSheet from './surface/BottomSheet';
import useMediaQuery from '../utils/useMediaQuery';

/**
 * AddPeopleModal — search-select people to add to a chat / invite.
 *
 * Two looks via `variant`:
 *   "default" — the Portal look (Sept 2026 messaging handoff, "New message / Add people
 *               modal"): a ring-bordered panel, radius 28, a pill search field with the
 *               disco frame on focus, round close button, pill CTA. Opens out of the button
 *               that asked for it when `fromRect` is given (its bounding rect). On phones
 *               (< 768) the same body rides the surface kit's bottom sheet instead.
 *   "portal"  — gold Portal Noir styling (Signals), rendered as a self-contained overlay
 *               wrapped in .portal-scope.
 *
 * `searchFn(query, limit)` overrides the people source (defaults to the global friend
 * search; Portal Noir passes portalService.searchPortalMembers). `suggestions` are shown
 * under `suggestionsLabel` while the query is empty; `ringFor(userId)` draws a PortalRing
 * on anyone with something live. `onAdd(userIds, picked)` gets the picked people too.
 */
export default function AddPeopleModal({
  isOpen,
  onClose,
  onAdd,
  excludeUserIds = [],
  single = false,
  title = 'Add people',
  placeholder = 'Search people by name or @handle…',
  ctaLabel = 'Add',
  showCount = true,
  variant = 'default',
  searchFn = null,
  fromRect = null,
  suggestions = [],
  suggestionsLabel = 'PORTALS LIVE NOW',
  ringFor = null,
}) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [focused, setFocused] = useState(false);
  const [closing, setClosing] = useState(false);
  const phone = useMediaQuery('(max-width: 767px)');
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const scrimRef = useRef(null);

  const doSearch = searchFn || ((q, limit) => friendService.searchPeople(q, limit));

  useEffect(() => {
    if (!isOpen) { setSearch(''); setResults([]); setPicked([]); setClosing(false); }
  }, [isOpen]);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      const r = await doSearch(search.trim(), 20);
      if (r.success) setResults(r.data || []);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // The panel opens out of the button that asked for it.
  useLayoutEffect(() => {
    if (!isOpen || variant !== 'default' || phone) return;
    revealModal({ overlay: overlayRef.current, panel: panelRef.current, scrim: scrimRef.current, fromRect });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || phone) return undefined;   // the sheet handles Escape itself
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const close = async () => {
    if (closing) return;
    if (variant === 'default' && !phone) {
      setClosing(true);
      await dismissModal({ panel: panelRef.current, scrim: scrimRef.current });
    }
    onClose?.();
  };

  const excluded = new Set([...(excludeUserIds || []), ...picked.map((p) => p.userId)]);
  const shown = (search.trim() ? results : suggestions).filter((u) => !excluded.has(u.userId));
  const suggesting = !search.trim() && shown.length > 0;

  const pick = (u) => {
    if (single) { onAdd([u.userId], [u]); return; }
    setPicked((p) => [...p, u]);
    setSearch('');
    setResults([]);
  };
  const unpick = (id) => setPicked((p) => p.filter((x) => x.userId !== id));

  const confirm = async () => {
    if (picked.length === 0) return;
    setBusy(true);
    await onAdd(picked.map((p) => p.userId), picked);
    setBusy(false);
  };

  const ctaText = showCount && picked.length > 0 ? `${ctaLabel} ${picked.length}` : ctaLabel;

  // ── Portal Noir (gold) variant — self-contained overlay ─────────────────────────
  if (variant === 'portal') {
    if (!isOpen) return null;
    const faint = 'rgba(236,231,218,0.34)';
    const line = 'rgba(236,231,218,0.12)';
    return (
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(6,6,7,0.74)', backdropFilter: 'blur(7px)', zIndex: 80, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto' }}
        onClick={onClose}
      >
        <div className="portal-scope portal-card" style={{ width: '100%', maxWidth: 560, padding: 22, marginTop: 40 }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <p className="portal-label" style={{ margin: 0 }}>{title.toUpperCase()}</p>
            <button onClick={onClose} className="portal-mono" style={{ background: 'none', border: 'none', color: faint, cursor: 'pointer', fontSize: 11 }}>CLOSE ×</button>
          </div>

          <input
            autoFocus
            type="text"
            className="portal-input"
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginBottom: 14 }}
          />

          {picked.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              {picked.map((p) => (
                <span key={p.userId} className="portal-chip active" onClick={() => unpick(p.userId)} style={{ cursor: 'pointer' }}>
                  {(p.displayName || '').toUpperCase()} ×
                </span>
              ))}
            </div>
          )}

          {shown.length > 0 && (
            <div style={{ maxHeight: 320, overflowY: 'auto', border: `1px solid ${line}`, marginBottom: 16 }}>
              {shown.map((u) => (
                <button
                  key={u.userId}
                  type="button"
                  onClick={() => pick(u)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: `1px solid ${line}`, cursor: 'pointer' }}
                >
                  <div className="portal-avatar" style={{ width: 36, height: 36 }}>
                    {u.avatarUrl ? <img src={u.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="portal-serif" style={{ fontSize: 14, color: '#ECE7DA' }}>{u.displayName}</div>
                    <div className="portal-mono" style={{ fontSize: 9, color: faint }}>
                      {u.handle ? `@${u.handle}` : ''}{u.location ? `${u.handle ? ' · ' : ''}${u.location.toUpperCase()}` : ''}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose} className="btn">CANCEL</button>
            <button onClick={confirm} disabled={busy || picked.length === 0} className="btn btn--gold">
              {busy ? 'SENDING…' : ctaText.toUpperCase()}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Default (Portal) variant ────────────────────────────────────────────────────

  const fieldStyle = {
    display: 'flex', alignItems: 'center', height: 40, borderRadius: 999,
    ...(focused
      ? discoFrame({ fill: '#fff' })
      : search
        ? { border: '2px solid transparent', background: fillFor('open') }
        : { border: '2px solid transparent', background: '#F2F2F2' }),
  };
  if (focused) fieldStyle.borderRadius = 999;

  const closeButton = (
    <button
      type="button"
      onClick={close}
      aria-label="Close"
      className="flex items-center justify-center hover:bg-black hover:text-white"
      style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid #000', background: '#fff', color: '#000', padding: 0, transition: 'background-color 200ms, color 200ms' }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square"><path d="M6 18L18 6M6 6l12 12" /></svg>
    </button>
  );
  const body = (
    <div style={{ padding: 24, paddingTop: 0 }}>
      {!single && picked.length > 0 && (
        <div className="flex flex-wrap" style={{ gap: 8, marginBottom: 12 }}>
          {picked.map((p) => (
            <button
              key={p.userId}
              type="button"
              onClick={() => unpick(p.userId)}
              className="flex items-center font-mono"
              style={{ gap: 6, borderRadius: 999, border: '2px solid #000', background: '#fff', fontSize: 12, padding: '4px 8px' }}
            >
              {p.displayName} <b>×</b>
            </button>
          ))}
        </div>
      )}

      <label style={fieldStyle}>
        {/* No autofocus on a phone: a focused field opens the keyboard on the same frame the
            sheet mounts, so the slide never plays and the keys land on top of it. One tap on
            the field is the expected gesture there. */}
        <input
          autoFocus={!phone}
          className="w-full min-w-0 border-0 bg-transparent font-mono placeholder:text-gray-400 focus:outline-none focus:ring-0"
          style={{ fontSize: 14, padding: '8px 14px' }}
          placeholder={placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
      </label>

      {suggesting && (
        <div className="font-mono font-bold" style={{ marginTop: 12, fontSize: 10, letterSpacing: '.1em', color: '#6B7280' }}>{suggestionsLabel}</div>
      )}

      {shown.length > 0 && (
        <div className="overflow-y-auto" style={{ marginTop: 8, borderRadius: 18, maxHeight: 232, background: '#fff' }}>
          {shown.map((u) => {
            const ring = ringFor?.(u.userId);
            return (
              <button
                key={u.userId}
                type="button"
                onClick={() => pick(u)}
                className="flex w-full items-center text-left hover:bg-[#F5F5F5]"
                style={{ gap: 12, padding: '8px 12px', background: '#fff' }}
              >
                {ring ? (
                  <PortalRing {...ring} size={36} />
                ) : (
                  <span className="flex flex-shrink-0 items-center justify-center overflow-hidden font-mono text-xs font-bold" style={{ width: 32, height: 32, borderRadius: 9, background: '#FFCCE6' }}>
                    {u.avatarUrl ? <img src={u.avatarUrl} alt="" className="h-full w-full object-cover" /> : (u.displayName || '?').charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block truncate font-mono font-semibold" style={{ fontSize: 14 }}>{u.displayName}</span>
                  {u.handle && <span className="block truncate font-mono" style={{ fontSize: 11, color: '#6B7280' }}>@{u.handle}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!single && (
        <div className="flex justify-end" style={{ gap: 8, marginTop: 16 }}>
          <button type="button" className="font-mono font-bold hover:text-black" style={{ border: 0, background: 'transparent', color: '#6B7280', fontSize: 12, letterSpacing: '.05em', padding: '8px 12px' }} onClick={close}>CANCEL</button>
          <button
            type="button"
            className="font-mono font-bold uppercase"
            style={{ borderRadius: 999, border: '2px solid #000', background: '#000', color: '#fff', fontSize: 12, letterSpacing: '.05em', padding: '8px 16px', opacity: picked.length > 0 && !busy ? 1 : 0.4, transition: 'opacity 200ms' }}
            disabled={busy || picked.length === 0}
            onClick={confirm}
          >
            {ctaText}
          </button>
        </div>
      )}
    </div>
  );

  // Phones: the same body rides the app's bottom sheet, which owns its own scrim, slide and
  // Escape handling — so it stays mounted through its close animation.
  if (phone) {
    return (
      <BottomSheet open={isOpen} onClose={onClose} title={title} header={false} zIndex={120} testId="add-people-sheet">
        <div className="flex items-center justify-between" style={{ padding: '16px 24px' }}>
          <h2 className="m-0 font-mono font-bold uppercase" style={{ fontSize: 18, letterSpacing: '.05em' }}>{title}</h2>
          {closeButton}
        </div>
        {body}
      </BottomSheet>
    );
  }

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      // z-[120]: above every full-screen surface in the app (composer 95, story viewer 100).
      className="fixed inset-0 safe-inset z-[120] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div ref={scrimRef} onClick={close} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)' }} />
      <div
        ref={panelRef}
        data-modal-panel
        className="relative flex w-full flex-col"
        style={{ maxWidth: 448, borderRadius: 28, border: '2px solid transparent', background: fillFor('open'), color: '#000' }}
      >
        <div className="flex items-center justify-between" style={{ padding: '16px 24px' }}>
          <h2 className="m-0 font-mono font-bold uppercase" style={{ fontSize: 18, letterSpacing: '.05em' }}>{title}</h2>
          {closeButton}
        </div>
        {body}
      </div>
    </div>
  );
}
